/* istanbul ignore file */
/* eslint-disable max-lines */
import { useState, useEffect, useRef, useCallback } from 'react';
import uuid from 'react-uuid';
import { faCamera } from '@fortawesome/pro-light-svg-icons/faCamera';
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast';
import { ToastType, ToastPosition } from '@hotmart/space-lib-events/dist/toast/enum';
import { faSquareCaretRight } from '@fortawesome/pro-light-svg-icons/faSquareCaretRight';
import Button from '@hotmart/space-lib/dist/components/button';
import { HttpCodes } from '@hotmart/space-lib/dist/constants';

import { createPost } from 'src/client/publication';
import { StringUtils, NO_BREAK_SPACE } from 'src/utils/string';
import LinkPreview from 'src/components/LinkPreview';
import useRequest from 'src/hook/useRequest';
import { MetaTagEndpoints, MetaTags, MetaTagService } from 'src/services/metaTag';
import { MEDIAS_PROCESSING_KEY } from 'src/utils/constants';
import { Surveys, SurvicateUtils } from 'src/utils/survicate';
import { ShowSchedulePostModalBtn } from 'src/pages/CommunityDetails/ShowSchedulePostModalBtn';
import { useFeatureFlag } from 'src/hook/useFeatureFlag';
import { useSchedulePostForm } from 'src/hook/useSchedulePostForm';
import { ApiError, queryClient } from 'src/config/request';
import {
  Actions,
  Entities,
  EventNames,
  ResultType,
  ResultsTypes,
  ScreenNames
} from 'src/utils/datahub/enums';
import { useUser } from 'src/hook/useUser';

import { canUserSchedulePost } from 'src/utils/user';
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext';
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume';
import { useOnboarding } from 'src/pages/Onboarding/hooks/useOnboarding';
import { OnboardingEndpoints } from 'src/services/onboarding';
import { useOptimizely } from 'src/hook/useOptimizely';
import { IMAGES } from '../../assets';
import i18n from '../../i18n';
import { PostProps, usePosts } from '../../hook/usePosts';
import { LinkUtils } from '../../utils/link';
import Icon from '../Icon';
import { PostType } from '../../hook/usePosts';
import { UploadResult } from '../dropZoneUploader';
import { ArrivedFrom } from '../courseCommunityAlert/courseCommunityAlert.enums';
import PreviewMedia from './PreviewMedia';
import { Editor, AvatarContainer } from './styles';
import InputMarkdownTitle from './title';
import { PropInputMarkdown, PropsCreatePost } from './types';
import { useCreatePostNotification } from './hooks/useCreatePostNotification';
import PublishCallToAction from './PublishCallToAction';
import { UploadBox } from './components';
import { useTrackEvents } from './hooks';

const InputMarkdown = ({
  openMarkdown,
  placeholder,
  communityId,
  setOpenInputMarkdown,
  setFilters,
  onSuccess,
  suggestedPost
}: PropInputMarkdown) => {
  const {
    refreshPage,
    postsProcessing,
    setLoading,
    setCurrentPage,
    setRefreshPage,
    setPostsProcessing,
    startCheckPostStatus,
    setMyPostsChecked
  } = usePosts();
  const { user, slug, permissionsUser, avatar } = useUser();
  const { community } = useCommunityDetails();
  const { sendCreatePostEvent } = useTrackEvents();
  const {
    onboardingSteps: {
      communityAdmin: { communityPostCreate }
    }
  } = useOnboarding();

  const editorRef = useRef<HTMLDivElement>(null);
  const divMarkDown = useRef<HTMLDivElement>(null);
  const [openUpload, setOpenUpload] = useState(false);
  const [currentMedia, setCurrentMedia] = useState('');
  const [mediaType, setMediaType] = useState(PostType.TEXT);
  const [uploading, setUploading] = useState(false);
  const [isLoadingPublishingPost, setIsLoadingPublishingPost] = useState<boolean>(false);
  const [publication, setPublication] = useState<PropsCreatePost>({
    uuid: String(uuid()),
    title: '',
    postType: 4,
    collectionId: communityId || community.id
  });

  const enableMembershipCommunityOnboardingFF = useFeatureFlag(
    'enable_membership_community_onboarding'
  );

  const enableMembershipCommunityScheduleFF = useFeatureFlag(
    'enable_membership_community_schedule'
  );
  const { sendDataHubConsume } = useSendDataHubConsume();
  const { formData, resetForm } = useSchedulePostForm();
  const sendCreatePostNotification = useCreatePostNotification();
  const { optimizely } = useOptimizely();

  const neededMetaTags = [MetaTags.IMAGE_TAG, MetaTags.TITLE_TAG, MetaTags.URL_TAG];

  const {
    data: linkPreviewData,
    isError: linkPreviewIsError,
    isLoading: linkPreviewIsLoading
  } = useRequest(
    `${MetaTagEndpoints.linkPreview()}/${publication.link}`,
    () => MetaTagService.getLinkPreviewMetatag(publication.link as string, neededMetaTags),
    {
      enabled: publication.postType === PostType.URL && Boolean(publication.link)
    }
  );

  const handleMediaDimensions = (url: string) => {
    const img = new Image();

    img.onload = (event: Event) => {
      const image = event.target as HTMLImageElement;

      setPublication((prevState) => {
        return {
          ...prevState,
          mediaWidth: image.width,
          mediaHeight: image.height
        };
      });
    };
    img.src = url;
  };

  const handleDropZoneSuccess = (files?: UploadResult[]) => {
    const fileObject = files?.[0];
    const url = fileObject?.blobUrl as string;
    const mediaKey = fileObject?.mediaKey as string;

    handleMediaDimensions(url);
    setCurrentMedia(url);
    setPublication((prevState) => {
      return { ...prevState, mediaKey, postType: mediaType };
    });
    setOpenUpload(!openUpload);
  };

  const handleDescription = () => {
    const description = StringUtils.replaceCharacterGloballyByKey(
      editorRef.current?.innerText || '',
      NO_BREAK_SPACE,
      ' '
    );

    setPublication((prevState) => {
      return { ...prevState, description };
    });
  };

  const handleOpenUploadMedia = (type: PostType) => {
    setMediaType(type);
    setOpenUpload(!openUpload);
  };

  const savePublicationTemp = (postId: number) => {
    toastEventDispatch({
      type: ToastType.INFO,
      message: i18n.t('toasts.video_in_processing'),
      position: ToastPosition.BOTTOM_RIGHT
    });

    const objPost: PostProps = {
      id: postId,
      authorName: user.name,
      authorImagePath: user.avatar as string,
      authorHasImage: false,
      authorMemberType: '',
      title: publication.title as string,
      description: publication.description || '',
      pubDate: new Date().getTime(),
      isAuthor: true,
      postType: PostType.VIDEO,
      authorId: Number(user.id),
      link: '',
      membershipInfo: {
        membershipId: ''
      },
      communityId: community.id
    };
    const newTemp = [...postsProcessing, objPost];
    setPostsProcessing(newTemp);
    localStorage.setItem(MEDIAS_PROCESSING_KEY, JSON.stringify(newTemp));
    startCheckPostStatus();
  };

  const classMarkdown = {
    container: openMarkdown ? '_flex-column' : '_flex-row _pr-4 _align-items-center',
    textarea: openMarkdown ? '_mb-4 _mx-10 _border-0' : '_pl-1 _w-full',
    boxMarkdown: openMarkdown ? '_border-top _border-gray-200 _p-4' : ''
  };

  const handleRemoveMedia = () => {
    setCurrentMedia('');
    setPublication((prevState) => {
      return { ...prevState, postType: PostType.TEXT, mediaKey: undefined };
    });
  };

  const removeLinkPreview = () => {
    setPublication((prevState) => {
      const newValue = { ...prevState, postType: 4 };

      delete newValue.link;
      delete newValue.linkDomain;
      delete newValue.linkImageUrl;
      delete newValue.linkTitle;

      return newValue;
    });
  };

  const clearInput = () => {
    setPublication({
      uuid: String(uuid()),
      title: '',
      postType: PostType.TEXT,
      collectionId: community.id
    });
    handleRemoveMedia();
    removeLinkPreview();
    if (editorRef.current) {
      editorRef.current.innerText = '';
    }
    resetForm();
  };

  const sendEvents = (result: ResultType, failureKind?: string) => {
    sendDataHubConsume({
      action: Actions.SCHEDULE,
      entity: Entities.SCHEDULED_POST,
      event: {
        screenName: ScreenNames.SCHEDULED_ACTIONS,
        eventName: EventNames.TRY_TO_SCHEDULE_A_POST,
        creator: user.name,
        userId: Number(user.id),
        communityId: Number(community.id),
        membershipSlug: slug,
        result,
        failureKind
      }
    });
  };

  const resetPostInfos = () => {
    setOpenInputMarkdown(false);
    setFilters?.({});
    setMyPostsChecked(false);
    clearInput();
    setCurrentPage(0);
    setRefreshPage(!refreshPage);
  };

  const createPublication = async () => {
    setLoading(true);
    setIsLoadingPublishingPost(true);

    try {
      const postId = await createPost(publication);

      if (!publication.scheduledPubDate) {
        toastEventDispatch({
          type: ToastType.SUCCESS,
          message: i18n.t('toasts.publish_success'),
          position: ToastPosition.BOTTOM_RIGHT
        });

        sendCreatePostEvent({
          communityId: Number(community.id),
          communityName: community.name
        });

        onSuccess?.();
      } else {
        sendCreatePostNotification('success');
        sendEvents(ResultsTypes.SUCCESS);
      }

      if (enableMembershipCommunityOnboardingFF && !communityPostCreate.communityFirstPost) {
        queryClient.invalidateQueries({
          queryKey: [OnboardingEndpoints.getOnboarding(community.id)]
        });
      }

      resetPostInfos();

      if (publication.postType === PostType.VIDEO) savePublicationTemp(Number(postId));

      SurvicateUtils.showSurvey(Surveys.PUBLICATION_MOTIVATION);

      const getArrivedFrom = () => {
        if (suggestedPost) return ArrivedFrom.CLASSROOM;
        return ScreenNames.COMMUNITY_NEW_FEED;
      };

      sendDataHubConsume({
        action: Actions.VIEW,
        entity: Entities.COMMUNITY,
        event: {
          screenName: ScreenNames.COMMUNITY_POST_DETAIL,
          eventName: EventNames.CLICK_TO_CREATE_POST,
          arrivedFrom: getArrivedFrom()
        }
      });

      optimizely.track(EventNames.CLICK_TO_CREATE_POST);
    } catch (error) {
      if (enableMembershipCommunityScheduleFF) {
        return sendCreatePostNotification(
          (error as ApiError).statusCode === HttpCodes.FORBIDDEN ? 'date_error' : 'error'
        );
      }

      sendEvents(ResultsTypes.FAILURE, (error as ApiError).message);

      throw new Error('Error when publishing');
    } finally {
      setLoading(false);
      setIsLoadingPublishingPost(false);
    }
  };

  const handleSuggestionOnboardingIA = useCallback(
    (event) => {
      const {
        data: { text, title }
      } = (event as CustomEvent)?.detail;

      if (title && text) {
        if (divMarkDown?.current) {
          divMarkDown.current.click();
          divMarkDown.current.focus();
        }

        setPublication((prev) => ({ ...prev, title: title, description: text }));

        if (editorRef.current) {
          (editorRef.current as HTMLDivElement).innerHTML = text;
        }
      }
    },
    [setPublication, divMarkDown.current, editorRef.current]
  );

  const handleSuggestionPost = () => {
    if (suggestedPost?.title) {
      if (divMarkDown?.current) {
        divMarkDown.current.click();
        divMarkDown.current.focus();
      }

      setPublication((prev) => ({
        ...prev,
        title: suggestedPost?.title,
        description: suggestedPost?.description
      }));

      if (editorRef.current) {
        (editorRef.current as HTMLDivElement).innerHTML = suggestedPost?.description;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('paste-suggestion-onboarding-ia', handleSuggestionOnboardingIA);

    handleSuggestionPost();

    return () => {
      window.removeEventListener('paste-suggestion-onboarding-ia', handleSuggestionOnboardingIA);
    };
  }, []);

  useEffect(() => {
    editorRef?.current?.addEventListener(
      'paste',
      function (e) {
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain');
        document.execCommand('insertHTML', false, text);
      },
      false
    );
  }, []);

  useEffect(() => {
    const debounceTyping = setTimeout(() => {
      if (publication.postType === PostType.TEXT || publication.postType === PostType.URL) {
        const link = LinkUtils.getFirstLinkFromText(publication.description || '');
        const postType = link ? 0 : 4;

        setPublication((prevState) => {
          return { ...prevState, link: link?.href, linkDomain: link?.host, postType };
        });
      }
    }, 1200);
    return () => clearTimeout(debounceTyping);
  }, [publication.description]);

  useEffect(() => {
    if (publication.postType === PostType.URL && !linkPreviewIsLoading) {
      setPublication((prevState) => {
        return {
          ...prevState,
          linkTitle: linkPreviewData?.title || prevState.linkDomain,
          linkImageUrl: linkPreviewData?.image
        };
      });
    }
  }, [linkPreviewData, publication.postType, setPublication]);

  useEffect(() => {
    if (linkPreviewIsError) removeLinkPreview();
  }, [linkPreviewIsError]);

  useEffect(() => {
    if (
      enableMembershipCommunityScheduleFF &&
      formData.pubDate &&
      formData.scheduledPubTimeZoneId
    ) {
      setPublication((prev) => ({
        ...prev,
        scheduledPubDate: formData.pubDate,
        scheduledPubTimeZoneId: formData.scheduledPubTimeZoneId
      }));
    }
  }, [enableMembershipCommunityScheduleFF, formData.pubDate, formData.scheduledPubTimeZoneId]);

  useEffect(() => {
    if (!openMarkdown) {
      resetForm();
    }
  }, [openMarkdown]);

  const disableSaveBtn = () => publication.postType === PostType.URL || currentMedia?.length > 0;

  const canUserCreateSchedulePost =
    permissionsUser?.actions && canUserSchedulePost(permissionsUser.actions);

  return (
    <div
      className={`_d-flex ${classMarkdown.container} _justify-content-between _w-full _border _border-gray-200 _rounded-lg`}
      ref={divMarkDown}
    >
      <div className="_d-flex _p-4">
        <AvatarContainer>
          <img
            src={avatar || IMAGES.AVATAR_OWNER_DEFAULT}
            className="img-avatar"
            alt="image profile"
          />
        </AvatarContainer>
        {openMarkdown && (
          <InputMarkdownTitle publication={publication} setPublication={setPublication} />
        )}
      </div>
      <Editor
        ref={editorRef}
        contentEditable
        data-test="description-input"
        data-name="description-input"
        placeholder={placeholder || i18n.t('input_publish.placeholder_description')}
        onInput={handleDescription}
        id="editor"
        className={`${classMarkdown.textarea}`}
      ></Editor>

      {openUpload && (
        <UploadBox
          classNames="_mb-2 _mx-4 _mx-md-10"
          mediaType={mediaType}
          onLoading={setUploading}
          onSuccess={handleDropZoneSuccess}
        />
      )}

      {currentMedia && (
        <PreviewMedia media={currentMedia} mediaType={mediaType} removeMedia={handleRemoveMedia} />
      )}

      {publication.postType === PostType.URL &&
        !linkPreviewIsError &&
        !linkPreviewIsLoading &&
        linkPreviewData && (
          <div className="_mx-10 _mb-5 _position">
            <LinkPreview
              link={publication.link as string}
              linkDomain={publication.linkDomain as string}
              title={linkPreviewData.title}
              image={linkPreviewData.image}
              handleRemove={removeLinkPreview}
            />
          </div>
        )}

      <div
        className={`_d-flex ${classMarkdown.boxMarkdown} _align-items-center _justify-content-between`}
      >
        {openMarkdown && (
          <div>
            <Button
              type="button"
              variation="tertiary"
              data-test="upload-image-button"
              onClick={() => handleOpenUploadMedia(PostType.IMAGE)}
              disabled={disableSaveBtn()}
            >
              <Icon icon={faCamera} />
              <span className="_ml-2">{i18n.t('input_publish.button_image')}</span>
            </Button>
            <Button
              type="button"
              variation="tertiary"
              data-test="upload-video-button"
              onClick={() => handleOpenUploadMedia(PostType.VIDEO)}
              disabled={disableSaveBtn()}
            >
              <Icon icon={faSquareCaretRight} />
              <span className="_ml-2">{i18n.t('input_publish.button_video')}</span>
            </Button>
          </div>
        )}

        <div className="_d-flex _align-items-center">
          {enableMembershipCommunityScheduleFF && canUserCreateSchedulePost ? (
            <ShowSchedulePostModalBtn isOpen={openMarkdown} />
          ) : null}

          <Button
            data-test="button-publish"
            onClick={createPublication}
            loading={isLoadingPublishingPost}
            disabled={!publication.title || uploading || isLoadingPublishingPost}
            variation="primary"
            className="_ml-3"
          >
            <PublishCallToAction />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InputMarkdown;
